import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isIOS(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

function isInStandaloneMode(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  try {
    return (
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      (window.navigator as any).standalone === true
    );
  } catch (e) {
    return false;
  }
}

function isMobile(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  useEffect(() => {
    // Don't show if already installed or dismissed
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed || isInStandaloneMode()) {
      return;
    }

    // Only show on mobile devices
    if (!isMobile()) {
      return;
    }

    // Check if iOS
    if (isIOS()) {
      setIsIOSDevice(true);
      setShowPrompt(true);
      return;
    }

    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOSDevice) {
      // iOS doesn't support programmatic install, just keep the prompt visible with instructions
      return;
    }

    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt (Android/Chrome)
    await deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`Install prompt outcome: ${outcome}`);

    // Clear the prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-dismissed', 'true');
    setShowPrompt(false);
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50">
      <div className="flex items-start gap-3">
        <img src="/icon-192.png" alt="App icon" className="w-12 h-12 rounded-lg flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900">Install Sparx Homework Tracker</h3>
          {isIOSDevice ? (
            <div className="text-sm text-gray-600 mt-1">
              <p className="mb-2">Add to your home screen for quick access:</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>Tap the Share button (box with arrow)</li>
                <li>Scroll and tap &quot;Add to Home Screen&quot;</li>
                <li>Tap &quot;Add&quot;</li>
              </ol>
            </div>
          ) : (
            <p className="text-sm text-gray-600 mt-1">
              Add to your home screen for quick access and offline support
            </p>
          )}
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        {!isIOSDevice && (
          <button
            onClick={handleInstall}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            Install
          </button>
        )}
        <button
          onClick={handleDismiss}
          className={`px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm ${isIOSDevice ? 'flex-1' : ''}`}
        >
          {isIOSDevice ? 'Got it' : 'Not now'}
        </button>
      </div>
    </div>
  );
}
